// eslint-disable-next-line import/no-cycle
import { logException } from './common/wscjs/analytics'
// eslint-disable-next-line import/no-cycle
import { getToken } from './common/wscjs/user-info-pre-cookies'
// eslint-disable-next-line import/no-cycle
import config, { getRestrictedUrl, getServiceUrl } from './config'
// eslint-disable-next-line import/no-cycle
import { fullLogout } from './login/loginUtils'

// for downloadUrlInTheSameTab define the timeout between every download when downloading multi content
const TimeBetweenDownloadClick = 2000

// When this is true, window should stop fetching stuff and prevent logout on Unathorized responses
// This value stays true until a window reload
let isStaleWindow = false

// Storage event to prevent Unathorized logout for other tabs
window.addEventListener('storage', (e) => {
  // only because MAMBA-YT still has refresh token, so this will avoid page refresh on multiple tabs
  if (!config.YOUTUBE_ENV) {
    if (e.key === config.USER_INFO_KEY) {
      isStaleWindow = true
    }
  }
})

window.addEventListener('focus', (e) => {
  if (isStaleWindow) {
    window.location.href = `${window.location.origin}${window.location.pathname}`
  }
})

export function onAuthChangeStartNetwork() {
  // Set value on next loop to not affect immediate fetch after this callback
  setTimeout(() => {
    isStaleWindow = true
  }, 0)
}

export async function fetchStaticContent(url, options = {}) {
  const error = new Error()

  const response = await fetch(url, options)

  if (!response.ok) {
    error.message = `${url} failed to fetch: "${response.statusText}" (${response.status})`
    error.response = response

    let responseText = 'Body failed to parse'

    try {
      responseText = await response.text()
    } catch (ex) {}

    const headers = Object.fromEntries(response.headers)

    logException(error, {
      responseText,
      headers,
    })

    return Promise.reject(error)
  }

  if (url.toLowerCase().endsWith('json')) {
    return response.json()
  }

  return response.arrayBuffer()
}

function request(path, options = {}) {
  let err = new Error()

  if (isStaleWindow) {
    return Promise.reject(new Error("Window's session is stale"))
  }

  const preventErrorLogging = options.preventErrorLogging || false
  const preventUnauthorizedLogout = options.preventUnauthorizedLogout || false
  const isNotUserInteractiveRequest = options.isNotUserInteractiveRequest || false

  let queryString = ''

  if (options.queryParams) {
    const filteredQueryParams = Object.fromEntries(
      Object.entries(options.queryParams).filter(
        ([, value]) => value !== null && value !== undefined,
      ),
    )

    queryString = '?' + new URLSearchParams(filteredQueryParams)
  }

  const url = path + queryString

  if (options.body instanceof FormData) {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest()

      request.responseType = 'json'

      request.onload = () => {
        if ([400, 500].includes(request.status)) {
          reject(request.response)
        } else {
          resolve(request.response)
        }
      }

      request.onerror = (error) => {
        reject(error)
      }

      request.upload.onprogress = options.onUploadProgress

      if (options.signal) {
        if (!(options.signal instanceof AbortSignal)) {
          throw new Error('options.signal is not an AbortSignal instance')
        }

        options.signal.onabort = (e) => {
          request.abort()

          const error = new DOMException('AbortError', 'AbortError')
          error.target = request

          reject(error)
        }
      }

      request.open(options.method, url)

      if (!config.IS_COOKIE_AUTH) {
        const token = getToken()

        if (Boolean(token)) {
          request.setRequestHeader('Authorization', `Bearer ${token}`)
        }
      } else {
        request.withCredentials = true
      }

      request.send(options.body)
    })
  }

  const token = !config.IS_COOKIE_AUTH && getToken()

  return fetch(url, {
    ...options,
    body: JSON.stringify(options.body),
    ...(config.IS_COOKIE_AUTH && !options.disableCredentials && { credentials: 'include' }),
    headers: {
      ...(token && {
        Authorization: `Bearer ${token}`,
      }),
      'Content-Type': 'application/json',
      ...(isNotUserInteractiveRequest && { 'X-WSC-Interactive': 'false' }),
      ...(config.IS_INTERNAL_COOKIE_AUTH_SITE && { 'X-WSC-Internal': 'true' }),
    },
  }).then((response) => {
    if (!response.ok) {
      return response
        .json()
        .catch(() => Promise.resolve(null))
        .then((errorBody) => {
          err.message = response.statusText
          err.status = response.status
          err.responseBody = errorBody

          // Unathorized
          if (response.status === 401) {
            if (isStaleWindow) {
              // The Auth Permissions were just replaced, we got a new cookie from the server, but the request
              // sent with the old one, so we got error 401, but we are right before a page reload,
              // so we just return an unresolved promise

              return new Promise(() => {})
            } else if (!preventUnauthorizedLogout) {
              fullLogout()

              // 'fullLogout' makes navigation to a new page, so we don't want to throw anything,
              // just return unresolved promise
              return new Promise(() => {})
            }

            return Promise.reject(err)
          }

          // Server Error / Bad Request
          if ([400, 500].includes(response.status) && !preventErrorLogging) {
            const method = options.method || 'GET'

            // Adding identifing title to 'Internal Server Error' for Sentry happiness
            err.message += ` (${method}: ${path})`

            logException(err, {
              url,
              method,
              responseBody: errorBody,
              requestBody: options.body,
            })
          }

          return Promise.reject(err)
        })
    }

    if (options.asText) {
      return response.text().catch(() => Promise.resolve(null))
    }

    return response.json().catch(() => Promise.resolve(null))
  })
}

const fetchClipro = (path, options) => request(`${config.CLIPRO_SERVICE}${path}`, options)

export const fetchAvgen = (path, options) => request(`${config.AVGEN_SERVICE}${path}`, options)

export const fetchBackoffice = (path, options) =>
  request(`${config.BACKOFFICE_SERVICE}${path}`, options)

export const fetchGraphics = (path, options) =>
  request(`${config.GRAPHICS_SERVICE}${path}`, options)

export const fetchAvgenServiceCore = (path, options) =>
  request(`${config.AVGEN_SERVICE_CORE}${path}`, options)

export const fetchRolesManagement = (path, options) =>
  request(`${config.ROLES_MANAGEMENT_SERVICE}${path}`, options)

export const fetchSettings = (path, options) =>
  request(`${config.SETTINGS_SERVICE}${path}`, options)

export const fetchUserManagement = (path, options) =>
  request(`${config.USER_MANAGEMENT_SERVICE}${path}`, options)

export const fetchPublish = (path, options) => request(`${config.PUBLISH_SERVICE}${path}`, options)

export const fetchOrc = (path, options) => request(`${config.ORC_SERVICE}${path}`, options)

export const fetchMovieCreator = (path, options) =>
  request(`${config.MOVIE_CREATOR_SERVICE}${path}`, options)

export const fetchMamba = (path, options) => request(`${config.MAMBA_SERVICE}${path}`, options)

export const fetchMambaZoomin = (path, options) =>
  request(`${config.MAMBA_ZOOMIN_SERVICE}${path}`, options)

export const fetchOffsetTagging = (path, options) =>
  request(`${config.OFFSET_TAGGING_SERVICE}${path}`, options)

export const fetchVideoAnalyzer = (path, options) =>
  request(`${config.VIDEO_ANALYZER_SERVICE}${path}`, options)

export const fetchSearch = (path, options) => request(`${config.SEARCH_SERVICE}${path}`, options)

export const fetchLogin = (path, options) => request(`${config.LOGIN_SERVICE}${path}`, options)

export const fetchVideo = (path, options) => request(`${config.VIDEO_SERVICE}${path}`, options)

export const fetchOperations = (path, options) =>
  request(`${config.OPERATIONS_SERVICE}${path}`, options)

export const fetchStories = (path, options) => request(`${config.STORIES_SERVICE}${path}`, options)

export const fetchStatic = (path, options) =>
  request(`${window.location.origin}/${config.WEBSITE}/static${path}`, options)

export const fetchAutoTag = (path, options) =>
  request(`${config.AUTO_TAGGING_SERVICE}${path}`, options)

export const fetchUpload = (path, options) => request(`${config.UPLOAD_SERVICE}${path}`, options)

export const fetchActionSettings = (path, options) =>
  request(`${config.ACTION_SETTINGS_SERVICE}${path}`, options)

export const fetchPlatform = (path, options) =>
  request(`${config.PLATFORM_SERVICE}${path}`, options)

export const fetchVideoSearch = (path, options) =>
  request(`${config.VIDEO_SEARCH_SERVICE}${path}`, options)

export const fetchPublicCliproSearch = (path, options) =>
  request(`${config.PUBLIC_CLIPRO_SEARCH_SERVICE}${path}`, options)

export const fetchPublicLibrarySearch = (path, options) =>
  request(`${config.PUBLIC_LIBRARY_SEARCH_SERVICE}${path}`, options)

export const fetchBlazeCms = (path, options) => request(`${config.BLAZE_SERVICE}/${path}`, options)

export const fetchBIDataHub = (path, options) => request(`${config.BI_DATAHUB}/${path}`, options)

export const fetchAutocomplete = (path, options) =>
  request(`${config.AUTOCOMPLETE}/${path}`, options)

export const fetchPrivateDownload = (path, options) =>
  request(`${config.DOWNLOAD_SERVICE}/private/${path}`, options)

export const fetchPublicDownload = (path, options) =>
  request(`${config.DOWNLOAD_SERVICE}/public/${path}`, options)

export const privateDownloadRequest = async (downloadRequest, authenticationType = 'private') => {
  const downloadJobResponse = await request(
    `${config.DOWNLOAD_SERVICE}/${authenticationType}/download`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: downloadRequest,
    },
  )

  if (downloadJobResponse.downloadJobId) {
    return downloadJobResponse.downloadJobId
  }

  return downloadJobResponse
}

export const downloadItemsInSameTab = (downloadUrls) => {
  const downloadNext = (downloadUrls, index, timeoutBetweenDownloads) => {
    if (index < downloadUrls.length) {
      downloadUrlInTheSameTab(downloadUrls[index])
      index++
      setTimeout(() => {
        downloadNext(downloadUrls, index, timeoutBetweenDownloads)
      }, timeoutBetweenDownloads)
    }
  }

  const downloadedIndex = 0

  return downloadNext(downloadUrls, downloadedIndex, TimeBetweenDownloadClick)
}

export function downloadUrlInTheSameTab(downloadUrl) {
  const downloadLink = document.createElement('a')

  downloadLink.href = downloadUrl
  downloadLink.setAttribute('download', '')
  downloadLink.target = 'downloadIframe'
  downloadLink.click()
}

export const fetchPlayer = (params, options) => {
  const queryParams = new URLSearchParams({ ...params })

  return fetch(`${config.DOWNLOAD_SERVICE}/public/player/?${queryParams.toString()}`, options)
}

export const fetchBi = (path, options) => request(`${config.BI_COLLECTOR_SERVICE}${path}`, options)

export const fetchAssets = (path, options) => request(`${config.ASSETS_SERVICE}${path}`, options)

export const fetchPlatformSearch = (path, options) =>
  request(`${config.PLATFORM_SEARCH_SERVICE}${path}`, options)

export const fetchRestricted = (restrictedGateway, serviceName, path, options) =>
  request(`${getRestrictedUrl(serviceName, restrictedGateway)}${path}`, options)

export const fetchRegular = (serviceName, path, options) =>
  request(`${getServiceUrl(serviceName)}${path}`, options)

export const fetchAlgo = (path, options) => request(`${config.ALGO_SERVICE}${path}`, options)

export const fetchClosedCaptions = (path, options) => {
  return request(`${config.CLOSED_CAPTIONS_SERVICE}${path}`, options)
}

export const fetchGameRecap = (path, options) => {
  return request(`${config.GAME_RECAP_SERVICE}${path}`, options)
}

export const fetchTextToVideo = (path, options) => {
  return request(`${config.ARTICLE_TO_VIDEO_SERVICE}${path}`, options)
}

export const fetchImport = (path, options) => request(`${config.IMPORT_SERVICE}${path}`, options)

export const fetchDistribution = (path, options) =>
  request(`${config.DISTRIBUTION_SERVICE}${path}`, options)

export const fetchRwithServiceUrl = (serviceUrl, path, options) => {
  return request(`${serviceUrl}/${path}`, options)
}

export default fetchClipro
